/**
 * Localization: English, Spanish, French, Portuguese, Italian.
 * Persists selected language in localStorage and applies to elements with data-i18n="key".
 */
export type Locale = 'en' | 'es' | 'fr' | 'pt' | 'it';

export const LANGUAGES: { code: Locale; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

const STORAGE_KEY = 'wordsearch-locale';

const translations: Record<string, Record<Locale, string>> = {
    // Main menu
    'menu.start': { en: 'START GAME', es: 'INICIAR JUEGO', fr: 'JOUER', pt: 'INICIAR JOGO', it: 'INIZIA GIOCO' },
    'menu.premium': { en: 'PREMIUM MODE', es: 'MODO PREMIUM', fr: 'MODE PREMIUM', pt: 'MODO PREMIUM', it: 'MODO PREMIUM' },
    'menu.howToPlay': { en: 'HOW TO PLAY', es: 'CÓMO JUGAR', fr: 'COMMENT JOUER', pt: 'COMO JOGAR', it: 'COME GIOCARE' },
    'menu.stats': { en: 'YOUR STATS', es: 'TUS ESTADÍSTICAS', fr: 'VOS STATS', pt: 'AS SUAS ESTATÍSTICAS', it: 'LE TUE STATISTICHE' },
    'menu.badges': { en: 'BADGES', es: 'INSIGNIAS', fr: 'BADGES', pt: 'INSÍGNIAS', it: 'BADGE' },
    'menu.language': { en: 'Language', es: 'Idioma', fr: 'Langue', pt: 'Idioma', it: 'Lingua' },
    'menu.premiumEnabled': { en: 'PREMIUM ENABLED', es: 'PREMIUM ACTIVADO', fr: 'PREMIUM ACTIVÉ', pt: 'PREMIUM ATIVADO', it: 'PREMIUM ATTIVO' },
    'menu.credits': { en: 'Credits', es: 'Créditos', fr: 'Crédits', pt: 'Créditos', it: 'Crediti' },
    'menu.options': { en: 'Options', es: 'Opciones', fr: 'Options', pt: 'Opções', it: 'Opzioni' },
    'consent.title': { en: 'Analytics', es: 'Analítica', fr: 'Analytique', pt: 'Análise', it: 'Analitica' },
    'consent.message': {
        en: 'We use Google Analytics to understand how the game is used (pages visited, features clicked). Data is anonymized and not sold. You can accept or decline.',
        es: 'Usamos Google Analytics para entender cómo se usa el juego (páginas visitadas, funciones usadas). Los datos son anónimos y no se venden. Puedes aceptar o rechazar.',
        fr: 'Nous utilisons Google Analytics pour comprendre l\'usage du jeu (pages vues, actions). Les données sont anonymisées et non vendues. Vous pouvez accepter ou refuser.',
        pt: 'Usamos o Google Analytics para perceber como o jogo é usado (páginas e ações). Os dados são anonimizados e não são vendidos. Pode aceitar ou recusar.',
        it: 'Usiamo Google Analytics per capire come viene usato il gioco (pagine e azioni). I dati sono anonimizzati e non venduti. Puoi accettare o rifiutare.',
    },
    'consent.accept': { en: 'Accept', es: 'Aceptar', fr: 'Accepter', pt: 'Aceitar', it: 'Accetta' },
    'consent.decline': { en: 'Decline', es: 'Rechazar', fr: 'Refuser', pt: 'Recusar', it: 'Rifiuta' },
    'feedback.title': { en: 'SEND FEEDBACK', es: 'ENVIAR FEEDBACK', fr: 'ENVOYER UN AVIS', pt: 'ENVIAR FEEDBACK', it: 'INVIA FEEDBACK' },
    'feedback.lead': {
        en: 'Tell us what you think — bugs, ideas, or anything that would make Explowords better.',
        es: 'Cuéntanos qué piensas: errores, ideas o lo que mejoraría Explowords.',
        fr: 'Dites-nous ce que vous en pensez — bugs, idées ou tout ce qui améliorerait Explowords.',
        pt: 'Diga-nos o que acha — erros, ideias ou o que melhoraria o Explowords.',
        it: 'Dicci cosa ne pensi — bug, idee o tutto ciò che migliorerebbe Explowords.',
    },
    'feedback.username': { en: 'Your name', es: 'Tu nombre', fr: 'Votre nom', pt: 'O seu nome', it: 'Il tuo nome' },
    'feedback.email': { en: 'Email (optional, for reply)', es: 'Email (opcional, para responder)', fr: 'E-mail (optionnel)', pt: 'Email (opcional)', it: 'Email (opzionale)' },
    'feedback.category': { en: 'Category', es: 'Categoría', fr: 'Catégorie', pt: 'Categoria', it: 'Categoria' },
    'feedback.categoryBug': { en: 'Bug / something broken', es: 'Error / algo roto', fr: 'Bug / problème', pt: 'Erro / algo partido', it: 'Bug / qualcosa non funziona' },
    'feedback.categorySuggestion': { en: 'Suggestion', es: 'Sugerencia', fr: 'Suggestion', pt: 'Sugestão', it: 'Suggerimento' },
    'feedback.categoryPraise': { en: 'Praise / what you like', es: 'Elogio / lo que te gusta', fr: 'Compliment', pt: 'Elogio', it: 'Complimento' },
    'feedback.categoryOther': { en: 'Other', es: 'Otro', fr: 'Autre', pt: 'Outro', it: 'Altro' },
    'feedback.message': { en: 'Your feedback', es: 'Tu mensaje', fr: 'Votre message', pt: 'A sua mensagem', it: 'Il tuo messaggio' },
    'feedback.send': { en: 'SEND', es: 'ENVIAR', fr: 'ENVOYER', pt: 'ENVIAR', it: 'INVIA' },
    'feedback.cancel': { en: 'CANCEL', es: 'CANCELAR', fr: 'ANNULER', pt: 'CANCELAR', it: 'ANNULLA' },
    'feedback.sending': { en: 'Sending…', es: 'Enviando…', fr: 'Envoi…', pt: 'A enviar…', it: 'Invio…' },
    'feedback.success': { en: 'Thanks! Your feedback was sent.', es: '¡Gracias! Tu feedback se ha enviado.', fr: 'Merci ! Votre message a été envoyé.', pt: 'Obrigado! O feedback foi enviado.', it: 'Grazie! Il feedback è stato inviato.' },
    'feedback.errorSend': { en: 'Could not send. Try again later.', es: 'No se pudo enviar. Inténtalo más tarde.', fr: 'Échec de l\'envoi. Réessayez plus tard.', pt: 'Não foi possível enviar. Tente mais tarde.', it: 'Invio non riuscito. Riprova più tardi.' },
    'feedback.errorUsername': { en: 'Please enter your name.', es: 'Introduce tu nombre.', fr: 'Indiquez votre nom.', pt: 'Indique o seu nome.', it: 'Inserisci il tuo nome.' },
    'feedback.errorMessage': { en: 'Please describe your feedback.', es: 'Describe tu feedback.', fr: 'Décrivez votre message.', pt: 'Descreva o seu feedback.', it: 'Descrivi il tuo feedback.' },
    'feedback.unconfigured': {
        en: 'Feedback is not configured on this build. Contact info@daydreamsoftware.es directly.',
        es: 'El feedback no está configurado en esta versión. Escribe a info@daydreamsoftware.es.',
        fr: 'Les retours ne sont pas configurés sur cette version. Contactez info@daydreamsoftware.es.',
        pt: 'O feedback não está configurado nesta versão. Contacte info@daydreamsoftware.es.',
        it: 'Il feedback non è configurato in questa build. Scrivi a info@daydreamsoftware.es.',
    },
    // Game mode
    'mode.title': { en: 'GAME MODE', es: 'MODO DE JUEGO', fr: 'MODE DE JEU', pt: 'MODO DE JOGO', it: 'MODALITÀ DI GIOCO' },
    'mode.gameOfTheDay': { en: 'GAME OF THE DAY', es: 'JUEGO DEL DÍA', fr: 'JEU DU JOUR', pt: 'JOGO DO DIA', it: 'GIOCO DEL GIORNO' },
    'mode.dailySummary': { en: "TODAY'S RESULT", es: 'RESULTADO DE HOY', fr: "RÉSULTAT D'AUJOURD'HUI", pt: 'RESULTADO DE HOJE', it: 'RISULTATO DI OGGI' },
    'mode.previousDays': { en: 'PREVIOUS DAY GAMES', es: 'JUEGOS DE DÍAS ANTERIORES', fr: 'JEUX DES JOURS PRÉCÉDENTS', pt: 'JOGOS DE DIAS ANTERIORES', it: 'GIOCHI GIORNI PRECEDENTI' },
    'mode.randomGame': { en: 'CUSTOM MATCH', es: 'PARTIDA PERSONALIZADA', fr: 'PARTIE PERSONNALISÉE', pt: 'PARTIDA PERSONALIZADA', it: 'PARTITA PERSONALIZZATA' },
    'mode.tutorial': { en: 'TUTORIAL', es: 'TUTORIAL', fr: 'TUTORIEL', pt: 'TUTORIAL', it: 'TUTORIAL' },
    'mode.createPuzzle': { en: 'CREATE PUZZLE', es: 'CREAR PUZZLE', fr: 'CRÉER UN PUZZLE', pt: 'CRIAR PUZZLE', it: 'CREA PUZZLE' },
    'badge.inDevelopment': { en: 'In development', es: 'En desarrollo', fr: 'En développement', pt: 'Em desenvolvimento', it: 'In sviluppo' },
    'badge.premium': { en: 'Premium', es: 'Premium', fr: 'Premium', pt: 'Premium', it: 'Premium' },
    'splash.tagline': { en: 'Daily word mines in 3D.', es: 'Minas de palabras 3D diarias.', fr: 'Mines de mots 3D du jour.', pt: 'Minas de palavras 3D diárias.', it: 'Miniere di parole 3D giornaliere.' },
    'splash.start': { en: 'START GAME', es: 'INICIAR JUEGO', fr: 'JOUER', pt: 'INICIAR JOGO', it: 'INIZIA GIOCO' },
    'splash.title': { en: 'Find the words. Explode the rest!', es: '¡Encuentra las palabras. Explota el resto!', fr: 'Trouvez les mots. Faites exploser le reste !', pt: 'Encontre as palavras. Exploda o resto!', it: 'Trova le parole. Fai esplodere il resto!' },
    'earlyAccess.splashLine': {
        en: 'Early access — Premium features unlocked until v1.0',
        es: 'Acceso anticipado — funciones Premium hasta la v1.0',
        fr: 'Accès anticipé — fonctions Premium jusqu\'à la v1.0',
        pt: 'Acesso antecipado — funções Premium até v1.0',
        it: 'Accesso anticipato — funzioni Premium fino alla v1.0',
    },
    'earlyAccess.menuLine': {
        en: 'EARLY ACCESS — Premium unlocked until v1.0',
        es: 'ACCESO ANTICIPADO — Premium hasta la v1.0',
        fr: 'ACCÈS ANTICIPÉ — Premium jusqu\'à la v1.0',
        pt: 'ACESSO ANTECIPADO — Premium até v1.0',
        it: 'ACCESSO ANTICIPATO — Premium fino alla v1.0',
    },
    'modal.earlyAccessTitle': { en: 'EARLY ACCESS', es: 'ACCESO ANTICIPADO', fr: 'ACCÈS ANTICIPÉ', pt: 'ACESSO ANTECIPADO', it: 'ACCESSO ANTICIPATO' },
    'modal.earlyAccessBody': {
        en: 'Welcome to Explowords! During Early access (until v1.0), all Premium game modes are free: Previous days, Custom match, and Create puzzle. Thank you for playing while we polish the game!',
        es: '¡Bienvenido a Explowords! Durante el acceso anticipado (hasta la v1.0), todos los modos Premium son gratis: días anteriores, partida personalizada y crear puzzle. ¡Gracias por jugar mientras pulimos el juego!',
        fr: 'Bienvenue sur Explowords ! Pendant l\'accès anticipé (jusqu\'à la v1.0), tous les modes Premium sont gratuits : jours précédents, partie aléatoire et créer un puzzle. Merci de jouer pendant que nous peaufinons le jeu !',
        pt: 'Bem-vindo ao Explowords! Durante o acesso antecipado (até v1.0), todos os modos Premium são grátis: dias anteriores, jogo aleatório e criar puzzle. Obrigado por jogar enquanto aperfeiçoamos o jogo!',
        it: 'Benvenuto su Explowords! Durante l\'accesso anticipato (fino alla v1.0), tutte le modalità Premium sono gratuite: giorni precedenti, partita casuale e crea puzzle. Grazie per giocare mentre rifiniamo il gioco!',
    },
    'modal.earlyAccessOk': { en: 'GOT IT', es: 'ENTENDIDO', fr: 'COMPRIS', pt: 'ENTENDI', it: 'OK' },
    'mode.back': { en: 'BACK', es: 'VOLVER', fr: 'RETOUR', pt: 'VOLTAR', it: 'INDIETRO' },
    // Previous days
    'previousDays.title': { en: 'PREVIOUS DAY GAMES', es: 'JUEGOS DE DÍAS ANTERIORES', fr: 'JEUX DES JOURS PRÉCÉDENTS', pt: 'JOGOS DE DIAS ANTERIORES', it: 'GIOCHI GIORNI PRECEDENTI' },
    // Game UI
    'game.score': { en: 'Score:', es: 'Puntuación:', fr: 'Score :', pt: 'Pontuação:', it: 'Punteggio:' },
    'game.scoreLabel': { en: 'Score', es: 'Puntuación', fr: 'Score', pt: 'Pontuação', it: 'Punteggio' },
    'game.wordCountError': { en: '1 error', es: '1 error', fr: '1 erreur', pt: '1 erro', it: '1 errore' },
    'game.wordCountErrors': { en: '{n} errors', es: '{n} errores', fr: '{n} erreurs', pt: '{n} erros', it: '{n} errori' },
    'game.completed': { en: 'COMPLETED', es: 'COMPLETADO', fr: 'TERMINÉ', pt: 'CONCLUÍDO', it: 'COMPLETATO' },
    'game.pause': { en: 'Pause', es: 'Pausa', fr: 'Pause', pt: 'Pausar', it: 'Pausa' },
    'game.explode': { en: 'EXPLODE', es: 'EXPLOTAR', fr: 'EXPLOSION', pt: 'EXPLODIR', it: 'ESPLODI' },
    'camera.faceTop': { en: 'Up', es: 'Arriba', fr: 'Haut', pt: 'Cima', it: 'Sopra' },
    'camera.faceBottom': { en: 'Down', es: 'Abajo', fr: 'Bas', pt: 'Baixo', it: 'Sotto' },
    'camera.faceFront': { en: 'Front', es: 'Delante', fr: 'Devant', pt: 'Frente', it: 'Davanti' },
    'camera.faceBack': { en: 'Back', es: 'Atrás', fr: 'Arrière', pt: 'Trás', it: 'Dietro' },
    'camera.faceLeft': { en: 'Left', es: 'Izquierda', fr: 'Gauche', pt: 'Esquerda', it: 'Sinistra' },
    'camera.faceRight': { en: 'Right', es: 'Derecha', fr: 'Droite', pt: 'Direita', it: 'Destra' },
    'camera.faceReset': { en: 'Reset view', es: 'Restablecer', fr: 'Réinitialiser', pt: 'Repor vista', it: 'Ripristina' },
    'camera.panelLabel': {
        en: 'Camera faces',
        es: 'Caras de la cámara',
        fr: 'Faces de la caméra',
        pt: 'Faces da câmara',
        it: 'Facce della camera',
    },
    'camera.hintTop': { en: '↑ Top', es: '↑ Arriba', fr: '↑ Haut', pt: '↑ Cima', it: '↑ Sopra' },
    'camera.hintBottom': { en: '↓ Bottom', es: '↓ Abajo', fr: '↓ Bas', pt: '↓ Baixo', it: '↓ Sotto' },
    'camera.hintLeft': { en: '← Left', es: '← Izquierda', fr: '← Gauche', pt: '← Esquerda', it: '← Sinistra' },
    'camera.hintRight': { en: '→ Right', es: '→ Derecha', fr: '→ Droite', pt: '→ Direita', it: '→ Destra' },
    'camera.hintTopTitle': {
        en: 'Move to top face (world up)',
        es: 'Ir a la cara superior (arriba absoluto)',
        fr: 'Aller à la face du haut (haut absolu)',
        pt: 'Ir para a face superior (cima absoluta)',
        it: 'Vai alla faccia superiore (sopra assoluto)',
    },
    'camera.hintBottomTitle': {
        en: 'Move to bottom face (world down)',
        es: 'Ir a la cara inferior (abajo absoluto)',
        fr: 'Aller à la face du bas (bas absolu)',
        pt: 'Ir para a face inferior (baixo absoluto)',
        it: 'Vai alla faccia inferiore (sotto assoluto)',
    },
    'camera.hintLeftTitle': {
        en: 'Move to left face (world left)',
        es: 'Ir a la cara izquierda (izquierda absoluta)',
        fr: 'Aller à la face gauche (gauche absolue)',
        pt: 'Ir para a face esquerda (esquerda absoluta)',
        it: 'Vai alla faccia sinistra (sinistra assoluta)',
    },
    'camera.hintFront': { en: 'Front', es: 'Delante', fr: 'Devant', pt: 'Frente', it: 'Davanti' },
    'camera.hintBack': { en: 'Back', es: 'Atrás', fr: 'Arrière', pt: 'Trás', it: 'Dietro' },
    'camera.hintFrontTitle': {
        en: 'Move to front face (world forward)',
        es: 'Ir a la cara delantera (adelante absoluto)',
        fr: 'Aller à la face avant (avant absolu)',
        pt: 'Ir para a face frontal (frente absoluta)',
        it: 'Vai alla faccia anteriore (avanti assoluto)',
    },
    'camera.hintBackTitle': {
        en: 'Move to back face (world backward)',
        es: 'Ir a la cara trasera (atrás absoluto)',
        fr: 'Aller à la face arrière (arrière absolu)',
        pt: 'Ir para a face traseira (trás absoluto)',
        it: 'Vai alla faccia posteriore (indietro assoluto)',
    },
    'game.explodeSelection': { en: 'EXPLODE SELECTION', es: 'EXPLOTAR SELECCIÓN', fr: 'EXPLOSION SÉLECTION', pt: 'EXPLODIR SELEÇÃO', it: 'ESPLODI SELEZIONE' },
    'game.explodeAllLetter': { en: 'EXPLODE ALL', es: 'EXPLOTAR TODAS LAS', fr: 'EXPLOSION TOUS LES', pt: 'EXPLODIR TODOS OS', it: 'ESPLODI TUTTE LE' },
    'game.explodeAllLetterFull': {
        en: 'EXPLODE ALL "{letter}"s',
        es: 'EXPLOTAR TODAS LAS "{letter}"',
        fr: 'EXPLOSION TOUS LES "{letter}"',
        pt: 'EXPLODIR TODOS OS "{letter}"',
        it: 'ESPLODI TUTTE LE "{letter}"',
    },
    'game.showAllLetterFull': {
        en: 'SHOW ALL "{letter}"s',
        es: 'MOSTRAR TODAS LAS "{letter}"',
        fr: 'AFFICHER TOUS LES "{letter}"',
        pt: 'MOSTRAR TODOS OS "{letter}"',
        it: 'MOSTRA TUTTE LE "{letter}"',
    },
    'game.cancel': { en: 'Cancel', es: 'Cancelar', fr: 'Annuler', pt: 'Cancelar', it: 'Annulla' },
    'game.resetView': { en: 'Reset View', es: 'Restablecer vista', fr: 'Réinitialiser la vue', pt: 'Redefinir vista', it: 'Reimposta vista' },
    'game.frontView': { en: 'Front View', es: 'Vista frontal', fr: 'Vue de face', pt: 'Vista frontal', it: 'Vista frontale' },
    'game.topView': { en: 'Top View', es: 'Vista superior', fr: 'Vue du dessus', pt: 'Vista superior', it: 'Vista dall\'alto' },
    'game.sideView': { en: 'Side View', es: 'Vista lateral', fr: 'Vue de côté', pt: 'Vista lateral', it: 'Vista laterale' },
    // Config modal
    'config.title': { en: 'CUSTOM MATCH SETTINGS', es: 'PARTIDA PERSONALIZADA', fr: 'PARTIE PERSONNALISÉE', pt: 'PARTIDA PERSONALIZADA', it: 'PARTITA PERSONALIZZATA' },
    'config.gridSize': { en: 'Base Grid Size:', es: 'Tamaño de cuadrícula:', fr: 'Taille de grille :', pt: 'Tamanho da grade:', it: 'Dimensione griglia:' },
    'config.customDimensions': {
        en: 'Custom dimensions (X × Y × Z)',
        es: 'Dimensiones personalizadas (X × Y × Z)',
        fr: 'Dimensions personnalisées (X × Y × Z)',
        pt: 'Dimensões personalizadas (X × Y × Z)',
        it: 'Dimensioni personalizzate (X × Y × Z)',
    },
    'config.wordCount': { en: 'Number of Words:', es: 'Número de palabras:', fr: 'Nombre de mots :', pt: 'Número de palavras:', it: 'Numero di parole:' },
    'config.wordLength': { en: 'Word length (dictionary):', es: 'Longitud de palabra (diccionario):', fr: 'Longueur des mots (dictionnaire) :', pt: 'Comprimento da palavra (dicionário):', it: 'Lunghezza parola (dizionario):' },
    'config.wordLanguage': { en: 'Word language:', es: 'Idioma de las palabras:', fr: 'Langue des mots :', pt: 'Idioma das palavras:', it: 'Lingua delle parole:' },
    'config.wordLanguageHint': {
        en: 'Daily puzzles always use your profile language (menu button).',
        es: 'El juego del día siempre usa el idioma de perfil (botón de idioma).',
        fr: 'Le jeu du jour utilise toujours la langue du profil (bouton langue).',
        pt: 'O jogo do dia usa sempre o idioma do perfil (botão de idioma).',
        it: 'Il gioco del giorno usa sempre la lingua del profilo (pulsante lingua).',
    },
    'config.letters': { en: 'letters', es: 'letras', fr: 'lettres', pt: 'letras', it: 'lettere' },
    'config.startGame': { en: 'START GAME', es: 'INICIAR JUEGO', fr: 'JOUER', pt: 'INICIAR JOGO', it: 'INIZIA GIOCO' },
    'config.hardcore': { en: 'Hardcore: lose if you explode a word tile', es: 'Hardcore: pierdes si explotas una letra de palabra', fr: 'Hardcore : vous perdez si vous faites exploser une lettre d\'un mot', pt: 'Hardcore: você perde se explodir uma letra de palavra', it: 'Hardcore: perdi se fai esplodere una lettera di una parola' },
    'config.timeAttack': { en: 'Time attack', es: 'Contrarreloj', fr: 'Contre la montre', pt: 'Ataque ao tempo', it: 'Attacco a tempo' },
    'config.timeLimit': { en: 'Time limit', es: 'Tiempo límite', fr: 'Temps limite', pt: 'Tempo limite', it: 'Tempo limite' },
    'config.seconds': { en: 'seconds', es: 'segundos', fr: 'secondes', pt: 'segundos', it: 'secondi' },
    // Modals
    'modal.close': { en: 'CLOSE', es: 'CERRAR', fr: 'FERMER', pt: 'FECHAR', it: 'CHIUDI' },
    'modal.options': { en: 'OPTIONS', es: 'OPCIONES', fr: 'OPTIONS', pt: 'OPÇÕES', it: 'OPZIONI' },
    'modal.sound': { en: 'Sound effects', es: 'Efectos de sonido', fr: 'Effets sonores', pt: 'Efeitos sonoros', it: 'Effetti sonori' },
    'modal.premium': { en: 'PREMIUM MODE', es: 'MODO PREMIUM', fr: 'MODE PREMIUM', pt: 'MODO PREMIUM', it: 'MODO PREMIUM' },
    'modal.premiumDev': { en: 'Premium mode is currently in development.', es: 'El modo premium está en desarrollo.', fr: 'Le mode premium est en cours de développement.', pt: 'O modo premium está em desenvolvimento.', it: 'La modalità premium è in sviluppo.' },
    'modal.premiumEnabledDefault': { en: 'It is enabled by default for everyone while we build it.', es: 'Está activado por defecto para todos mientras lo desarrollamos.', fr: 'Il est activé par défaut pour tous pendant que nous le construisons.', pt: 'Está ativado por padrão para todos enquanto o desenvolvemos.', it: 'È attivato di default per tutti mentre lo sviluppiamo.' },
    'modal.premiumRequired': { en: 'This mode requires Premium. Subscribe to unlock.', es: 'Este modo requiere Premium. Suscríbete para desbloquear.', fr: 'Ce mode nécessite Premium. Abonnez-vous pour débloquer.', pt: 'Este modo requer Premium. Subscreva para desbloquear.', it: 'Questa modalità richiede Premium. Abbonati per sbloccare.' },
    'modal.premiumSubscriptionActive': { en: 'Your Premium subscription is active. You have access to all game modes and no ads.', es: 'Tu suscripción Premium está activa. Tienes acceso a todos los modos y sin anuncios.', fr: 'Votre abonnement Premium est actif. Vous avez accès à tous les modes et sans publicité.', pt: 'A sua assinatura Premium está ativa. Tem acesso a todos os modos e sem anúncios.', it: 'Il tuo abbonamento Premium è attivo. Hai accesso a tutti i modi di gioco e senza annunci.' },
    'modal.premiumEarlyAccessActive': {
        en: 'Early access is active until v1.0. All Premium game modes are unlocked.',
        es: 'El acceso anticipado está activo hasta la v1.0. Todos los modos Premium están desbloqueados.',
        fr: 'L\'accès anticipé est actif jusqu\'à la v1.0. Tous les modes Premium sont débloqués.',
        pt: 'O acesso antecipado está ativo até v1.0. Todos os modos Premium estão desbloqueados.',
        it: 'L\'accesso anticipato è attivo fino alla v1.0. Tutte le modalità Premium sono sbloccate.',
    },
    'modal.premiumLead': { en: 'Unlock the full Explowords experience — more modes, no ads, and puzzles on your terms.', es: 'Desbloquea Explowords al completo: más modos, sin anuncios y puzzles a tu medida.', fr: 'Débloquez Explowords : plus de modes, sans pub, des puzzles à votre rythme.', pt: 'Desbloqueie o Explowords completo: mais modos, sem anúncios e puzzles ao seu gosto.', it: 'Sblocca Explowords: più modalità, niente annunci e puzzle come vuoi.' },
    'modal.premiumFeatModes': { en: 'Previous days, custom matches, and create-your-own puzzles', es: 'Días anteriores, partida personalizada y crear puzzle', fr: 'Jours précédents, partie personnalisée et création de puzzles', pt: 'Dias anteriores, partida personalizada e criar puzzle', it: 'Giorni precedenti, partita personalizzata e crea puzzle' },
    'modal.premiumFeatAds': { en: 'Play without banner ads', es: 'Jugar sin banners publicitarios', fr: 'Jouer sans bannières publicitaires', pt: 'Jogar sem banners de anúncios', it: 'Gioca senza banner pubblicitari' },
    'modal.premiumFeatPass': { en: '30-minute pass or monthly subscription', es: 'Pase de 30 minutos o suscripción mensual', fr: 'Pass 30 min ou abonnement mensuel', pt: 'Passe de 30 min ou subscrição mensal', it: 'Pass 30 min o abbonamento mensile' },
    'modal.premiumTimedActive': { en: 'Premium pass active — time left: {time}', es: 'Pase Premium activo — tiempo restante: {time}', fr: 'Pass Premium actif — temps restant : {time}', pt: 'Passe Premium ativo — tempo restante: {time}', it: 'Pass Premium attivo — tempo rimasto: {time}' },
    'modal.premiumPaymentsOff': { en: 'Online payments are not enabled yet. Premium will be available in the store soon.', es: 'Los pagos en línea aún no están activos. Premium estará disponible en la tienda pronto.', fr: 'Les paiements en ligne ne sont pas encore actifs. Premium arrive bientôt dans la boutique.', pt: 'Os pagamentos online ainda não estão ativos. Premium em breve na loja.', it: 'I pagamenti online non sono ancora attivi. Premium presto nel negozio.' },
    'modal.premiumGoStore': { en: 'View plans & pricing', es: 'Ver planes y precios', fr: 'Voir offres et tarifs', pt: 'Ver planos e preços', it: 'Vedi piani e prezzi' },
    'modal.premiumSubscribeCta': { en: 'Subscribe to Premium to unlock: Previous Day Games, Random Game, Create Puzzle, and an ad-free experience.', es: 'Suscríbete a Premium para desbloquear: Partidas de días anteriores, Partida aleatoria, Crear puzzle y sin anuncios.', fr: 'Abonnez-vous à Premium pour débloquer : Parties des jours précédents, Partie aléatoire, Créer un puzzle et sans publicité.', pt: 'Subscreva ao Premium para desbloquear: Jogos de dias anteriores, Jogo aleatório, Criar puzzle e sem anúncios.', it: 'Abbonati a Premium per sbloccare: Partite dei giorni precedenti, Partita casuale, Crea puzzle e senza annunci.' },
    'modal.subscribe': { en: 'SUBSCRIBE', es: 'SUSCRIBIRSE', fr: 'S\'ABONNER', pt: 'SUBSCREVER', it: 'ABBONATI' },
    'store.title': { en: 'Explowords Store', es: 'Tienda Explowords', fr: 'Boutique Explowords', pt: 'Loja Explowords', it: 'Negozio Explowords' },
    'store.back': { en: 'Back to game', es: 'Volver al juego', fr: 'Retour au jeu', pt: 'Voltar ao jogo', it: 'Torna al gioco' },
    'store.disabled': { en: 'Payments are currently disabled. Check back later or contact us if you run this site.', es: 'Los pagos están desactivados. Vuelve más tarde.', fr: 'Les paiements sont désactivés. Revenez plus tard.', pt: 'Os pagamentos estão desativados. Volte mais tarde.', it: 'I pagamenti sono disattivati. Torna più tardi.' },
    'store.passTitle': { en: '30-minute Premium pass', es: 'Pase Premium 30 minutos', fr: 'Pass Premium 30 minutes', pt: 'Passe Premium 30 min', it: 'Pass Premium 30 minuti' },
    'store.passDesc': { en: 'Try every Premium mode and play ad-free for half an hour. One-time purchase (Stripe coming soon).', es: 'Prueba todos los modos Premium y juega sin anuncios media hora. Pago único (Stripe próximamente).', fr: 'Essayez tous les modes Premium sans pub pendant 30 minutes. Achat unique (Stripe bientôt).', pt: 'Experimente todos os modos Premium sem anúncios por 30 min. Compra única (Stripe em breve).', it: 'Prova tutte le modalità Premium senza annunci per 30 min. Acquisto una tantum (Stripe in arrivo).' },
    'store.monthTitle': { en: 'Monthly Premium', es: 'Premium mensual', fr: 'Premium mensuel', pt: 'Premium mensal', it: 'Premium mensile' },
    'store.monthDesc': { en: 'Unlimited access to all modes, no ads, and future Premium features. Billed monthly (Stripe coming soon).', es: 'Acceso ilimitado a todos los modos, sin anuncios y funciones futuras. Facturación mensual (Stripe próximamente).', fr: 'Accès illimité à tous les modes, sans pub. Facturation mensuelle (Stripe bientôt).', pt: 'Acesso ilimitado a todos os modos, sem anúncios. Faturação mensal (Stripe em breve).', it: 'Accesso illimitato a tutte le modalità, senza annunci. Fatturazione mensile (Stripe in arrivo).' },
    'store.comingSoon': { en: 'Checkout coming soon', es: 'Pago disponible pronto', fr: 'Paiement bientôt disponible', pt: 'Pagamento em breve', it: 'Pagamento in arrivo' },
    'store.passPrice': { en: 'One-time', es: 'Pago único', fr: 'Achat unique', pt: 'Pagamento único', it: 'Una tantum' },
    'store.monthPrice': { en: 'Per month', es: 'Al mes', fr: 'Par mois', pt: 'Por mês', it: 'Al mese' },
    'modal.howToPlay': { en: 'HOW TO PLAY', es: 'CÓMO JUGAR', fr: 'COMMENT JOUER', pt: 'COMO JOGAR', it: 'COME GIOCARE' },
    'modal.dailyTutorialIntro': {
        en: 'Welcome to the Game of the Day! Find words by selecting letters, or try the optional PERFECT run: explode only filler tiles—never select a word—and earn a bonus when the cube clears itself.',
        es: '¡Bienvenido al juego del día! Encuentra palabras seleccionando letras, o prueba el PERFECTO opcional: explota solo fichas de relleno—sin seleccionar ninguna palabra—y gana bonificación cuando el cubo se complete solo.',
        fr: 'Bienvenue au jeu du jour ! Trouvez des mots en sélectionnant des lettres, ou tentez le PARFAIT optionnel : exploser uniquement les tuiles de remplissage—sans sélectionner de mot—pour un bonus quand le cube se complète seul.',
        pt: 'Bem-vindo ao jogo do dia! Encontra palavras selecionando letras, ou tenta o PERFEITO opcional: explode só peças de preenchimento—sem selecionar nenhuma palavra—e ganha bónus quando o cubo se completa sozinho.',
        it: 'Benvenuto al gioco del giorno! Trova le parole selezionando le lettere, o prova il PERFETTO opzionale: esplodi solo tessere di riempimento—senza selezionare parole—e ottieni un bonus quando il cubo si completa da solo.',
    },
    'modal.howToPlay1': { en: 'Find all hidden words in the 3D grid. Words are in straight lines (along axes or diagonals).', es: 'Encuentra todas las palabras ocultas en la cuadrícula 3D. Las palabras están en líneas rectas (ejes o diagonales).', fr: 'Trouvez tous les mots cachés dans la grille 3D. Les mots sont en lignes droites (axes ou diagonales).', pt: 'Encontre todas as palavras ocultas na grade 3D. As palavras estão em linhas retas (eixos ou diagonais).', it: 'Trova tutte le parole nascoste nella griglia 3D. Le parole sono in linee rette (assi o diagonali).' },
    'modal.playLabel': { en: 'Play:', es: 'Jugar:', fr: 'Jouer :', pt: 'Jogar:', it: 'Gioca:' },
    'modal.howToPlay2': { en: 'Click a letter to start, then click another letter on the same line. If the segment spells a word (forward or backward), it counts as found.', es: 'Haz clic en una letra para empezar, luego en otra de la misma línea. Si el segmento forma una palabra (adelante o atrás), cuenta como encontrada.', fr: 'Cliquez sur une lettre pour commencer, puis sur une autre sur la même ligne. Si le segment forme un mot (avant ou arrière), il compte comme trouvé.', pt: 'Clique em uma letra para começar, depois em outra na mesma linha. Se o segmento formar uma palavra (para frente ou trás), conta como encontrada.', it: 'Clicca su una lettera per iniziare, poi su un\'altra sulla stessa linea. Se il segmento forma una parola (avanti o indietro), conta come trovata.' },
    'modal.riskLabel': { en: 'Risk:', es: 'Riesgo:', fr: 'Risque :', pt: 'Risco:', it: 'Rischio:' },
    'modal.howToPlay3': { en: 'Risk: You can explode tiles you don\'t need—but if you explode a letter that belongs to a hidden word, you lose!', es: 'Riesgo: Puedes explotar fichas que no necesites, pero si explotas una letra de una palabra oculta, ¡pierdes!', fr: 'Risque : Vous pouvez faire exploser les tuiles inutiles—mais si vous faites exploser une lettre d\'un mot caché, vous perdez !', pt: 'Risco: Você pode explodir peças que não precisa—mas se explodir uma letra de uma palavra oculta, perde!', it: 'Rischio: Puoi far esplodere le tessere non necessarie—ma se fai esplodere una lettera di una parola nascosta, perdi!' },
    'modal.howToPlay4': { en: 'Use the layer slider to hide outer layers and the view buttons to look at the cube from different sides.', es: 'Usa el control de capas para ocultar capas externas y los botones de vista para ver el cubo desde distintos lados.', fr: 'Utilisez le curseur de couches pour masquer les couches externes et les boutons de vue pour regarder le cube sous différents angles.', pt: 'Use o controle de camadas para ocultar camadas externas e os botões de vista para ver o cubo de diferentes lados.', it: 'Usa lo slider dei livelli per nascondere gli strati esterni e i pulsanti vista per guardare il cubo da diverse parti.' },
    'modal.howToPlay5': {
        en: 'Tip: right-click a letter to explode it instantly without selecting it first.',
        es: 'Consejo: clic derecho sobre una letra para explotarla al instante sin seleccionarla.',
        fr: 'Astuce : clic droit sur une lettre pour l\'exploser instantanément sans la sélectionner.',
        pt: 'Dica: clique direito numa letra para explodi-la na hora, sem selecionar.',
        it: 'Suggerimento: clic destro su una lettera per farla esplodere subito senza selezionarla.',
    },
    'modal.outcomesLabel': { en: 'Outcomes:', es: 'Resultados:', fr: 'Résultats :', pt: 'Resultados:', it: 'Esiti:' },
    'modal.howToPlayFinished': {
        en: 'FINISHED — You cleared all words still in play, but one or more words were lost after exploding a letter that belonged to them.',
        es: 'FINALIZADO — Completaste las palabras que aún podías encontrar, pero perdiste una o más al explotar una letra que formaba parte de ellas.',
        fr: 'TERMINÉ — Vous avez trouvé tous les mots encore possibles, mais un ou plusieurs mots ont été perdus en faisant exploser une de leurs lettres.',
        pt: 'CONCLUÍDO — Completaste todas as palavras ainda possíveis, mas perdeste uma ou mais ao explodir uma letra que lhes pertencia.',
        it: 'FINE — Hai completato tutte le parole ancora reperibili, ma ne hai persa una o più esplodendo una lettera che ne faceva parte.',
    },
    'modal.howToPlayCompleted': {
        en: 'COMPLETED — You found every word in the puzzle without losing any.',
        es: 'COMPLETADO — Encontraste todas las palabras del puzzle sin perder ninguna.',
        fr: 'COMPLÉTÉ — Vous avez trouvé tous les mots du puzzle sans en perdre aucun.',
        pt: 'COMPLETO — Encontraste todas as palavras do puzzle sem perder nenhuma.',
        it: 'COMPLETATO — Hai trovato tutte le parole del puzzle senza perderne nessuna.',
    },
    'modal.howToPlayPerfect': {
        en: 'PERFECT — You never selected a word: only filler letters were exploded until word tiles alone remained, then the rest auto-completed for a bonus score.',
        es: 'PERFECTO — No seleccionaste ninguna palabra: solo explotaste letras de relleno hasta que quedaron las de las palabras, y el resto se completó solo con bonificación.',
        fr: 'PARFAIT — Vous n\'avez sélectionné aucun mot : seules les lettres de remplissage ont été explosées, puis le reste s\'est auto-complété avec un bonus.',
        pt: 'PERFEITO — Não selecionaste nenhuma palavra: só explodiste letras de preenchimento até restarem as das palavras, e o resto completou-se sozinho com bónus.',
        it: 'PERFETTO — Non hai selezionato nessuna parola: hai esploso solo lettere di riempimento finché restavano quelle delle parole, poi il resto si è completato da solo con bonus.',
    },
    'modal.createPuzzle': { en: 'CREATE PUZZLE', es: 'CREAR PUZZLE', fr: 'CRÉER UN PUZZLE', pt: 'CRIAR PUZZLE', it: 'CREA PUZZLE' },
    'modal.createPuzzleDev': { en: 'Game mode in development. Here you will create cubes for other players to play.', es: 'Modo de juego en desarrollo. Aquí podrás crear cubos para que otros jueguen.', fr: 'Mode de jeu en développement. Ici vous créerez des cubes pour que d\'autres jouent.', pt: 'Modo de jogo em desenvolvimento. Aqui você criará cubos para outros jogarem.', it: 'Modalità in sviluppo. Qui potrai creare cubi per altri giocatori.' },
    'modal.accept': { en: 'ACCEPT', es: 'ACEPTAR', fr: 'ACCEPTER', pt: 'ACEITAR', it: 'ACCETTA' },
    'stats.title': { en: 'YOUR STATS', es: 'TUS ESTADÍSTICAS', fr: 'VOS STATISTIQUES', pt: 'AS SUAS ESTATÍSTICAS', it: 'LE TUE STATISTICHE' },
    'stats.tabDaily': { en: 'Daily', es: 'Diario', fr: 'Quotidien', pt: 'Diário', it: 'Giornaliero' },
    'stats.tabGlobal': { en: 'Global', es: 'Global', fr: 'Global', pt: 'Global', it: 'Globale' },
    'stats.tabDailyHint': {
        en: 'Game of the Day and previous days.',
        es: 'Juego del día y días anteriores.',
        fr: 'Jeu du jour et jours précédents.',
        pt: 'Jogo do dia e dias anteriores.',
        it: 'Gioco del giorno e giorni precedenti.',
    },
    'stats.tabGlobalHint': {
        en: 'All modes: daily, previous days, custom matches, and random games.',
        es: 'Todos los modos: diario, días anteriores, personalizadas y aleatorias.',
        fr: 'Tous les modes : quotidien, jours passés, personnalisé et aléatoire.',
        pt: 'Todos os modos: diário, dias anteriores, personalizadas e aleatórias.',
        it: 'Tutte le modalità: giornaliero, giorni passati, personalizzate e casuali.',
    },
    'stats.empty': {
        en: 'No games recorded yet. Finish a daily puzzle to start tracking your stats.',
        es: 'Aún no hay partidas registradas. Completa un puzzle diario para empezar.',
        fr: 'Aucune partie enregistrée. Terminez un puzzle du jour pour commencer.',
        pt: 'Ainda não há jogos registados. Complete um puzzle diário para começar.',
        it: 'Nessuna partita registrata. Completa un puzzle del giorno per iniziare.',
    },
    'stats.gamesPlayed': { en: 'Games played', es: 'Partidas jugadas', fr: 'Parties jouées', pt: 'Jogos jogados', it: 'Partite giocate' },
    'stats.gamesWon': { en: 'Games won', es: 'Partidas ganadas', fr: 'Parties gagnées', pt: 'Jogos ganhos', it: 'Partite vinte' },
    'stats.winRate': { en: 'Win rate', es: 'Tasa de victoria', fr: 'Taux de victoire', pt: 'Taxa de vitória', it: 'Percentuale vittorie' },
    'stats.wordsFound': { en: 'Words found', es: 'Palabras encontradas', fr: 'Mots trouvés', pt: 'Palavras encontradas', it: 'Parole trovate' },
    'stats.wordsLost': { en: 'Words lost', es: 'Palabras perdidas', fr: 'Mots perdus', pt: 'Palavras perdidas', it: 'Parole perse' },
    'stats.playTime': { en: 'Total play time', es: 'Tiempo de juego', fr: 'Temps de jeu', pt: 'Tempo de jogo', it: 'Tempo di gioco' },
    'stats.fastestWin': { en: 'Fastest win', es: 'Victoria más rápida', fr: 'Victoire la plus rapide', pt: 'Vitória mais rápida', it: 'Vittoria più veloce' },
    'stats.bestScore': { en: 'Best score', es: 'Mejor puntuación', fr: 'Meilleur score', pt: 'Melhor pontuação', it: 'Miglior punteggio' },
    'stats.avgWinScore': { en: 'Average win score', es: 'Puntuación media al ganar', fr: 'Score moyen (victoires)', pt: 'Pontuação média (vitórias)', it: 'Punteggio medio (vittorie)' },
    'stats.perfects': { en: 'Perfect runs', es: 'Perfectos', fr: 'Parfaits', pt: 'Perfeitos', it: 'Perfetti' },
    'stats.completed': { en: 'Completed (no errors)', es: 'Completados (sin errores)', fr: 'Complétés (sans erreur)', pt: 'Completos (sem erros)', it: 'Completati (senza errori)' },
    'stats.finished': { en: 'Finished (with lost words)', es: 'Finalizados (con palabras perdidas)', fr: 'Terminés (mots perdus)', pt: 'Concluídos (com palavras perdidas)', it: 'Finiti (con parole perse)' },
    'stats.losses': { en: 'Losses', es: 'Derrotas', fr: 'Défaites', pt: 'Derrotas', it: 'Sconfitte' },
    'stats.tilesExploded': { en: 'Tiles exploded', es: 'Fichas explotadas', fr: 'Tuiles explosées', pt: 'Peças explodidas', it: 'Tessere esplose' },
    'stats.currentStreak': { en: 'Current daily streak', es: 'Racha diaria actual', fr: 'Série quotidienne actuelle', pt: 'Sequência diária atual', it: 'Serie giornaliera attuale' },
    'stats.bestStreak': { en: 'Best daily streak', es: 'Mejor racha diaria', fr: 'Meilleure série quotidienne', pt: 'Melhor sequência diária', it: 'Miglior serie giornaliera' },
    'stats.byLanguage': { en: 'By language', es: 'Por idioma', fr: 'Par langue', pt: 'Por idioma', it: 'Per lingua' },
    'stats.badgesHeading': { en: 'Badges earned', es: 'Insignias conseguidas', fr: 'Badges obtenus', pt: 'Insígnias conquistadas', it: 'Badge ottenuti' },
    'modal.credits': { en: 'CREDITS', es: 'CRÉDITOS', fr: 'CRÉDITS', pt: 'CRÉDITOS', it: 'CREDITI' },
    'modal.creditsBy': { en: 'By', es: 'Por', fr: 'Par', pt: 'Por', it: 'Di' },
    'modal.paused': { en: 'PAUSED', es: 'PAUSADO', fr: 'EN PAUSE', pt: 'PAUSADO', it: 'IN PAUSA' },
    'modal.resume': { en: 'RESUME', es: 'REANUDAR', fr: 'REPRENDRE', pt: 'RETOMAR', it: 'RIPRENDI' },
    'modal.restart': { en: 'RESTART', es: 'REINICIAR', fr: 'REDÉMARRER', pt: 'REINICIAR', it: 'RIAVVIA' },
    'modal.backToMenu': { en: 'BACK TO MENU', es: 'VOLVER AL MENÚ', fr: 'RETOUR AU MENU', pt: 'VOLTAR AO MENU', it: 'TORNA AL MENU' },
    'modal.youWin': { en: 'YOU WIN!', es: '¡GANASTE!', fr: 'VOUS GAGNEZ !', pt: 'VOCÊ GANHOU!', it: 'HAI VINTO!' },
    'modal.finished': { en: 'FINISHED', es: 'FINALIZADO', fr: 'TERMINÉ', pt: 'CONCLUÍDO', it: 'FINE' },
    'modal.winCompleted': { en: 'COMPLETED', es: 'COMPLETADO', fr: 'COMPLÉTÉ', pt: 'COMPLETO', it: 'COMPLETATO' },
    'modal.perfect': { en: 'PERFECT', es: 'PERFECTO', fr: 'PARFAIT', pt: 'PERFEITO', it: 'PERFETTO' },
    'modal.subtitleNoErrors': { en: 'No errors', es: 'Sin errores', fr: 'Aucune erreur', pt: 'Sem erros', it: 'Nessun errore' },
    'modal.subtitleAllWordsCleared': { en: 'All Words cleared', es: 'Todas las palabras completadas', fr: 'Tous les mots trouvés', pt: 'Todas as palavras completas', it: 'Tutte le parole completate' },
    'modal.timeUp': { en: "Time's up!", es: '¡Se acabó el tiempo!', fr: 'Temps écoulé !', pt: 'Tempo esgotado!', it: 'Tempo scaduto!' },
    'modal.youLose': { en: 'YOU LOSE!', es: '¡PERDISTE!', fr: 'VOUS PERDEZ !', pt: 'VOCÊ PERDEU!', it: 'HAI PERSO!' },
    'modal.viewResult': { en: 'VIEW 3D RESULT', es: 'VER RESULTADO 3D', fr: 'VOIR LE RÉSULTAT 3D', pt: 'VER RESULTADO 3D', it: 'VEDI RISULTATO 3D' },
    'modal.viewSummary': { en: 'View result summary', es: 'Ver resumen del resultado', fr: 'Voir le résumé', pt: 'Ver resumo do resultado', it: 'Vedi riepilogo risultato' },
    'modal.victoryMaster': { en: 'VICTORY MASTER', es: 'VICTORIA MAGISTRAL', fr: 'VICTOIRE MAGISTRALE', pt: 'VITÓRIA MAGISTRAL', it: 'VITTORIA MAGISTRALE' },
    'modal.specialVictory': { en: 'SPECIAL VICTORY', es: 'VICTORIA ESPECIAL', fr: 'VICTOIRE SPÉCIALE', pt: 'VITÓRIA ESPECIAL', it: 'VITTORIA SPECIALE' },
    'modal.time': { en: 'Time:', es: 'Tiempo:', fr: 'Temps :', pt: 'Tempo:', it: 'Tempo:' },
    'modal.layers': { en: 'Layers', es: 'Capas', fr: 'Couches', pt: 'Camadas', it: 'Livelli' },
    // Share (game over)
    'share.title': { en: 'Share your result', es: 'Comparte tu resultado', fr: 'Partagez votre score', pt: 'Partilhe o resultado', it: 'Condividi il risultato' },
    'share.native': { en: 'Share…', es: 'Compartir…', fr: 'Partager…', pt: 'Partilhar…', it: 'Condividi…' },
    'share.copy': { en: 'Copy text', es: 'Copiar texto', fr: 'Copier', pt: 'Copiar', it: 'Copia' },
    'share.copyResult': {
        en: 'Copy result (emoji grid)',
        es: 'Copiar resultado (rejilla emoji)',
        fr: 'Copier le résultat (grille emoji)',
        pt: 'Copiar resultado (grelha emoji)',
        it: 'Copia risultato (griglia emoji)',
    },
    'share.copyFailed': {
        en: 'Could not copy — try again',
        es: 'No se pudo copiar — inténtalo de nuevo',
        fr: 'Copie impossible — réessayez',
        pt: 'Não foi possível copiar — tenta outra vez',
        it: 'Copia non riuscita — riprova',
    },
    'share.sendEmail': { en: 'Send by email', es: 'Enviar por email', fr: 'Envoyer par e-mail', pt: 'Enviar por email', it: 'Invia via email' },
    'share.emailPlaceholder': { en: 'friend@email.com (optional)', es: 'amigo@email.com (opcional)', fr: 'ami@email.com (optionnel)', pt: 'amigo@email.com (opcional)', it: 'amico@email.com (opzionale)' },
    'share.emailSubject': { en: 'My Explowords result', es: 'Mi resultado en Explowords', fr: 'Mon score Explowords', pt: 'O meu resultado Explowords', it: 'Il mio risultato Explowords' },
    'share.copied': { en: 'Copied to clipboard!', es: '¡Copiado al portapapeles!', fr: 'Copié !', pt: 'Copiado!', it: 'Copiato!' },
    'share.copiedImage': { en: 'Image copied!', es: '¡Imagen copiada!', fr: 'Image copiée !', pt: 'Imagem copiada!', it: 'Immagine copiata!' },
    'share.copyImage': { en: 'Copy image', es: 'Copiar imagen', fr: 'Copier l’image', pt: 'Copiar imagem', it: 'Copia immagine' },
    'share.statsTitle': { en: 'Share your stats', es: 'Compartir estadísticas', fr: 'Partager les stats', pt: 'Partilhar estatísticas', it: 'Condividi statistiche' },
    'share.backToStats': { en: 'Back to stats', es: 'Volver a estadísticas', fr: 'Retour aux stats', pt: 'Voltar às estatísticas', it: 'Torna alle statistiche' },
    'share.previewBrand': { en: 'EXPLOWORDS', es: 'EXPLOWORDS', fr: 'EXPLOWORDS', pt: 'EXPLOWORDS', it: 'EXPLOWORDS' },
    'share.previewDate': { en: 'Daily puzzle · {date}', es: 'Puzzle del día · {date}', fr: 'Puzzle du jour · {date}', pt: 'Puzzle do dia · {date}', it: 'Puzzle del giorno · {date}' },
    'share.previewWinHead': { en: 'Daily puzzle cleared!', es: '¡Puzzle del día completado!', fr: 'Puzzle du jour terminé !', pt: 'Puzzle do dia completo!', it: 'Puzzle del giorno completato!' },
    'share.previewLoseHead': { en: 'My run today', es: 'Mi partida de hoy', fr: 'Ma partie du jour', pt: 'A minha partida de hoje', it: 'La mia partita di oggi' },
    'share.previewScore': { en: '{score} points', es: '{score} puntos', fr: '{score} points', pt: '{score} pontos', it: '{score} punti' },
    'share.previewTime': { en: 'Time: {time}', es: 'Tiempo: {time}', fr: 'Temps : {time}', pt: 'Tempo: {time}', it: 'Tempo: {time}' },
    'share.previewWords': { en: 'Words: {found} / {total}', es: 'Palabras: {found} / {total}', fr: 'Mots : {found} / {total}', pt: 'Palavras: {found} / {total}', it: 'Parole: {found} / {total}' },
    'share.previewCtaWin': { en: 'Can you beat my score?', es: '¿Me superas?', fr: 'À vous de jouer !', pt: 'Consegues ultrapassar?', it: 'Riesci a battermi?' },
    'share.previewCtaLose': { en: 'Try the daily 3D word mine!', es: '¡Prueba la mina 3D del día!', fr: 'Essayez la mine 3D du jour !', pt: 'Experimenta a mina 3D do dia!', it: 'Prova la mina 3D del giorno!' },
    'share.previewPlay': { en: 'Play at', es: 'Juega en', fr: 'Jouer sur', pt: 'Joga em', it: 'Gioca su' },
    'share.statsHeadline': {
        en: 'My {tab} stats',
        es: 'Mis estadísticas ({tab})',
        fr: 'Mes stats ({tab})',
        pt: 'As minhas estatísticas ({tab})',
        it: 'Le mie statistiche ({tab})',
    },
    'share.statsEmailSubject': {
        en: 'My Explowords stats',
        es: 'Mis estadísticas de Explowords',
        fr: 'Mes stats Explowords',
        pt: 'As minhas estatísticas Explowords',
        it: 'Le mie statistiche Explowords',
    },
    'share.previewBadgesLabel': { en: 'Badges', es: 'Insignias', fr: 'Badges', pt: 'Insígnias', it: 'Badge' },
    'badges.title': { en: 'BADGES', es: 'INSIGNIAS', fr: 'BADGES', pt: 'INSÍGNIAS', it: 'BADGE' },
    'badges.intro': {
        en: 'Secondary objectives you can earn during a puzzle.',
        es: 'Objetivos secundarios que puedes conseguir durante la partida.',
        fr: 'Objectifs secondaires à accomplir pendant une partie.',
        pt: 'Objetivos secundários que pode conquistar durante a partida.',
        it: 'Obiettivi secondari da completare durante la partita.',
    },
    'badges.howToGet': { en: 'How to earn', es: 'Cómo conseguirla', fr: 'Comment l\'obtenir', pt: 'Como conquistar', it: 'Come ottenerlo' },
    'badges.noneThisRun': { en: 'No badges this run', es: 'Sin insignias en esta partida', fr: 'Aucun badge cette partie', pt: 'Sem insígnias nesta partida', it: 'Nessun badge in questa partita' },
    'badge.fragile.name': { en: 'Fragile', es: 'Frágil', fr: 'Fragile', pt: 'Frágil', it: 'Fragile' },
    'badge.fragile.desc': {
        en: 'Complete a puzzle without destroying any letter.',
        es: 'Completa un puzzle sin destruir ninguna letra.',
        fr: 'Terminez un puzzle sans détruire aucune lettre.',
        pt: 'Complete um puzzle sem destruir nenhuma letra.',
        it: 'Completa un puzzle senza distruggere nessuna lettera.',
    },
    'badge.fragile.how': {
        en: 'Finish the puzzle with zero exploded tiles.',
        es: 'Termina el puzzle sin haber explotado ninguna ficha.',
        fr: 'Terminez sans aucune tuile explosée.',
        pt: 'Termine sem explodir nenhuma peça.',
        it: 'Termina senza aver fatto esplodere alcuna tessera.',
    },
    'badge.destroyer.name': { en: 'Destroyer', es: 'Destructor', fr: 'Destructeur', pt: 'Destruidor', it: 'Distruttore' },
    'badge.destroyer.desc': {
        en: 'Finish with every filler letter destroyed.',
        es: 'Termina con todas las letras de relleno destruidas.',
        fr: 'Terminez en ayant détruit toutes les lettres de remplissage.',
        pt: 'Termine com todas as letras de preenchimento destruídas.',
        it: 'Termina avendo distrutto tutte le lettere di riempimento.',
    },
    'badge.destroyer.how': {
        en: 'At victory, no non-word letters remain on the grid.',
        es: 'Al ganar, no queda ninguna letra que no pertenezca a palabras.',
        fr: 'À la victoire, aucune lettre hors mots ne reste sur la grille.',
        pt: 'Na vitória, não restam letras que não pertencem a palavras.',
        it: 'Alla vittoria, non restano lettere che non appartengono a parole.',
    },
    'badge.careful.name': { en: 'Careful', es: 'Cuidadoso', fr: 'Prudent', pt: 'Cuidadoso', it: 'Attento' },
    'badge.careful.desc': {
        en: 'Find every word without losing any.',
        es: 'Encuentra todas las palabras sin perder ninguna.',
        fr: 'Trouvez tous les mots sans en perdre aucun.',
        pt: 'Encontre todas as palavras sem perder nenhuma.',
        it: 'Trova tutte le parole senza perderne nessuna.',
    },
    'badge.careful.how': {
        en: 'Win without having failed any word (zero lost words).',
        es: 'Gana sin haber fallado ninguna palabra (cero palabras perdidas).',
        fr: 'Gagnez sans avoir perdu aucun mot.',
        pt: 'Vença sem ter falhado nenhuma palavra.',
        it: 'Vinci senza aver perso nessuna parola.',
    },
    'badge.efficient.name': { en: 'Efficient', es: 'Eficiente', fr: 'Efficace', pt: 'Eficiente', it: 'Efficiente' },
    'badge.efficient.desc': {
        en: 'Finish in under 10 minutes.',
        es: 'Termina en menos de 10 minutos.',
        fr: 'Terminez en moins de 10 minutes.',
        pt: 'Termine em menos de 10 minutos.',
        it: 'Termina in meno di 10 minuti.',
    },
    'badge.efficient.how': {
        en: 'Complete the puzzle in under 10:00.',
        es: 'Completa el puzzle en menos de 10:00.',
        fr: 'Terminez le puzzle en moins de 10:00.',
        pt: 'Complete o puzzle em menos de 10:00.',
        it: 'Completa il puzzle in meno di 10:00.',
    },
    'badge.ultraEfficient.name': { en: 'Ultra-efficient', es: 'Ultraeficiente', fr: 'Ultra-efficace', pt: 'Ultraeficiente', it: 'Ultra-efficiente' },
    'badge.ultraEfficient.desc': {
        en: 'Finish in under 5 minutes.',
        es: 'Termina en menos de 5 minutos.',
        fr: 'Terminez en moins de 5 minutes.',
        pt: 'Termine em menos de 5 minutos.',
        it: 'Termina in meno di 5 minuti.',
    },
    'badge.ultraEfficient.how': {
        en: 'Complete the puzzle in under 5:00.',
        es: 'Completa el puzzle en menos de 5:00.',
        fr: 'Terminez le puzzle en moins de 5:00.',
        pt: 'Complete o puzzle em menos de 5:00.',
        it: 'Completa il puzzle in meno di 5:00.',
    },
    'share.textWin': {
        en: 'EXPLOWORDS\nDaily puzzle cleared!\n{score} points · Time {time}\nWords: {found}/{total}\nCan you beat my score?\n{url}',
        es: 'EXPLOWORDS\n¡Puzzle del día completado!\n{score} puntos · Tiempo {time}\nPalabras: {found}/{total}\n¿Me superas?\n{url}',
        fr: 'EXPLOWORDS\nPuzzle du jour terminé !\n{score} points · Temps {time}\nMots : {found}/{total}\nÀ vous de jouer !\n{url}',
        pt: 'EXPLOWORDS\nPuzzle do dia completo!\n{score} pontos · Tempo {time}\nPalavras: {found}/{total}\nConsegues ultrapassar?\n{url}',
        it: 'EXPLOWORDS\nPuzzle del giorno completato!\n{score} punti · Tempo {time}\nParole: {found}/{total}\nRiesci a battermi?\n{url}',
    },
    'share.textLose': {
        en: 'EXPLOWORDS\nMy run today\n{score} points · Words {found}/{total}\nTry the daily 3D word mine!\n{url}',
        es: 'EXPLOWORDS\nMi partida de hoy\n{score} puntos · Palabras {found}/{total}\n¡Prueba la mina 3D del día!\n{url}',
        fr: 'EXPLOWORDS\nMa partie du jour\n{score} points · Mots {found}/{total}\nEssayez la mine 3D du jour !\n{url}',
        pt: 'EXPLOWORDS\nA minha partida de hoje\n{score} pontos · Palavras {found}/{total}\nExperimenta a mina 3D do dia!\n{url}',
        it: 'EXPLOWORDS\nLa mia partita di oggi\n{score} punti · Parole {found}/{total}\nProva la mina 3D del giorno!\n{url}',
    },
    'share.viralHeaderDaily': {
        en: 'Explowords #{date} ({lang})',
        es: 'Explowords #{date} ({lang})',
        fr: 'Explowords #{date} ({lang})',
        pt: 'Explowords #{date} ({lang})',
        it: 'Explowords #{date} ({lang})',
    },
    'share.viralHeaderMode': {
        en: 'Explowords · {mode} ({lang})',
        es: 'Explowords · {mode} ({lang})',
        fr: 'Explowords · {mode} ({lang})',
        pt: 'Explowords · {mode} ({lang})',
        it: 'Explowords · {mode} ({lang})',
    },
    'share.viralModeRandom': {
        en: 'Random',
        es: 'Aleatorio',
        fr: 'Aléatoire',
        pt: 'Aleatório',
        it: 'Casuale',
    },
    'share.viralModeCustom': {
        en: 'Custom',
        es: 'Personalizado',
        fr: 'Personnalisé',
        pt: 'Personalizado',
        it: 'Personalizzato',
    },
    'share.viralModeDefault': {
        en: 'Puzzle',
        es: 'Puzzle',
        fr: 'Puzzle',
        pt: 'Puzzle',
        it: 'Puzzle',
    },
    'share.viralStatsWin': {
        en: '⭐ {outcome} · {score} pts · {time}',
        es: '⭐ {outcome} · {score} pts · {time}',
        fr: '⭐ {outcome} · {score} pts · {time}',
        pt: '⭐ {outcome} · {score} pts · {time}',
        it: '⭐ {outcome} · {score} pts · {time}',
    },
    'share.viralStatsLose': {
        en: '{score} pts · Words {found}/{total}',
        es: '{score} pts · Palabras {found}/{total}',
        fr: '{score} pts · Mots {found}/{total}',
        pt: '{score} pts · Palavras {found}/{total}',
        it: '{score} pts · Parole {found}/{total}',
    },
    'share.viralFooter': {
        en: '{url}',
        es: '{url}',
        fr: '{url}',
        pt: '{url}',
        it: '{url}',
    },
    'share.viralOutcomePerfect': {
        en: 'PERFECT',
        es: 'PERFECTO',
        fr: 'PARFAIT',
        pt: 'PERFEITO',
        it: 'PERFETTO',
    },
    'share.viralOutcomeCompleted': {
        en: 'COMPLETED',
        es: 'COMPLETADO',
        fr: 'TERMINÉ',
        pt: 'COMPLETO',
        it: 'COMPLETATO',
    },
    'share.viralOutcomeFinished': {
        en: 'FINISHED',
        es: 'FINALIZADO',
        fr: 'FINI',
        pt: 'FINALIZADO',
        it: 'FINITO',
    },
    'share.viralOutcomeLose': {
        en: 'TRY AGAIN',
        es: 'INTÉNTALO DE NUEVO',
        fr: 'RÉESSAYEZ',
        pt: 'TENTA OUTRA VEZ',
        it: 'RIPROVA',
    },
    'onboarding.rotate': {
        en: 'Drag the cube to rotate it. You can also tap the labels around the cube to jump to each face, or use the eye button (bottom-left) to open the camera menu.',
        es: 'Arrastra el cubo para rotarlo. También puedes pulsar las etiquetas alrededor del cubo para ir a cada cara, o usar el botón del ojo (abajo a la izquierda) para abrir el menú de cámara.',
        fr: 'Faites glisser le cube pour le tourner. Vous pouvez aussi toucher les étiquettes autour du cube pour aller à chaque face, ou utiliser le bouton œil (en bas à gauche) pour ouvrir le menu caméra.',
        pt: 'Arraste o cubo para rodar. Também podes tocar nas etiquetas à volta do cubo para ir a cada face, ou usar o botão do olho (canto inferior esquerdo) para abrir o menu de câmara.',
        it: 'Trascina il cubo per ruotarlo. Puoi anche toccare le etichette intorno al cubo per andare a ogni faccia, o usare il pulsante occhio (in basso a sinistra) per aprire il menu camera.',
    },
    'onboarding.select': {
        en: 'Tap a letter, then another on the same straight line to claim a word.',
        es: 'Toca una letra y otra en la misma línea recta para encontrar una palabra.',
        fr: 'Touchez une lettre, puis une autre sur la même ligne droite.',
        pt: 'Toque numa letra e noutra na mesma linha reta para formar uma palavra.',
        it: 'Tocca una lettera e un’altra sulla stessa linea retta.',
    },
    'onboarding.selectHighlight': {
        en: 'The word {word} is highlighted on the cube. You will select its first and last letter.',
        es: 'La palabra {word} está resaltada en el cubo. Seleccionarás su primera y última letra.',
        fr: 'Le mot {word} est surligné sur le cube. Vous allez sélectionner sa première et sa dernière lettre.',
        pt: 'A palavra {word} está destacada no cubo. Vais selecionar a primeira e a última letra.',
        it: 'La parola {word} è evidenziata sul cubo. Selezionerai la prima e l’ultima lettera.',
    },
    'onboarding.selectStart': {
        en: 'Tap the highlighted first letter of {word}.',
        es: 'Toca la primera letra resaltada de {word}.',
        fr: 'Touchez la première lettre surlignée de {word}.',
        pt: 'Toca na primeira letra destacada de {word}.',
        it: 'Tocca la prima lettera evidenziata di {word}.',
    },
    'onboarding.selectEnd': {
        en: 'Now tap the highlighted last letter to complete {word}.',
        es: 'Ahora toca la última letra resaltada para completar {word}.',
        fr: 'Touchez maintenant la dernière lettre surlignée pour compléter {word}.',
        pt: 'Agora toca na última letra destacada para completar {word}.',
        it: 'Ora tocca l’ultima lettera evidenziata per completare {word}.',
    },
    'onboarding.selectFirstLetter': {
        en: 'All possible lines from this letter are shown. The toolbar below lets you explode it, explode all matching letters, or highlight every matching letter.',
        es: 'Se muestran todas las líneas posibles desde esta letra. La barra inferior permite explotarla, explotar todas las iguales o resaltar todas las letras iguales.',
        fr: 'Toutes les lignes possibles depuis cette lettre s’affichent. La barre du bas permet de l’exploser, d’exploser toutes les lettres identiques ou de les surligner.',
        pt: 'São mostradas todas as linhas possíveis a partir desta letra. A barra inferior permite explodir, explodir todas as iguais ou mostrar todas as letras iguais.',
        it: 'Vengono mostrate tutte le linee possibili da questa lettera. La barra in basso permette di esploderla, esplodere tutte le uguali o evidenziare tutte le lettere uguali.',
    },
    'onboarding.explode': {
        en: 'Right-click the highlighted letter on the cube — not a hidden word letter!',
        es: 'Clic derecho sobre la letra resaltada en el cubo — ¡no explotes letras de palabras ocultas!',
        fr: 'Clic droit sur la lettre surlignée sur le cube — pas une lettre de mot caché !',
        pt: 'Clique direito na letra destacada no cubo — não explodas letras de palavras escondidas!',
        it: 'Clic destro sulla lettera evidenziata sul cubo — non lettere di parole nascoste!',
    },
    'onboarding.explodeTouch': {
        en: 'Select a letter, then tap EXPLODE on the toolbar — never explode a hidden word letter!',
        es: 'Elige una letra y pulsa EXPLOTAR en la barra — ¡no explotes letras de palabras ocultas!',
        fr: 'Sélectionnez une lettre, puis touchez EXPLOSER — ne détruisez pas une lettre de mot caché !',
        pt: 'Escolhe uma letra e toca EXPLODIR na barra — não explodas letras de palavras escondidas!',
        it: 'Seleziona una lettera e tocca ESPLODI — non far esplodere lettere di parole nascoste!',
    },
    'onboarding.layers': {
        en: 'Use the layer slider to hide outer shells and read inner letters.',
        es: 'Usa el slider de capas para ocultar capas exteriores y leer las letras interiores.',
        fr: 'Utilisez le curseur de couches pour masquer l’extérieur et lire les lettres intérieures.',
        pt: 'Use o slider de camadas para esconder as camadas exteriores e ler as letras interiores.',
        it: 'Usa lo slider dei livelli per nascondere gli strati esterni e leggere le lettere interne.',
    },
    'onboarding.finishWords': {
        en: 'Find the remaining words to finish the tutorial.',
        es: 'Encuentra las demás palabras para terminar el tutorial.',
        fr: 'Trouvez les mots restants pour terminer le tutoriel.',
        pt: 'Encontra as palavras restantes para concluir o tutorial.',
        it: 'Trova le parole rimanenti per completare il tutorial.',
    },
    'onboarding.next': {
        en: 'Next',
        es: 'Siguiente',
        fr: 'Suivant',
        pt: 'Seguinte',
        it: 'Avanti',
    },
    'onboarding.prev': {
        en: 'Previous',
        es: 'Anterior',
        fr: 'Précédent',
        pt: 'Anterior',
        it: 'Indietro',
    },
    'onboarding.skip': {
        en: 'Skip tutorial',
        es: 'Saltar tutorial',
        fr: 'Passer',
        pt: 'Saltar',
        it: 'Salta',
    },
    'onboarding.done': {
        en: 'Got it!',
        es: '¡Entendido!',
        fr: 'Compris !',
        pt: 'Percebi!',
        it: 'Capito!',
    },
    'tutorial.offerTitle': {
        en: 'New to Explowords?',
        es: '¿Nuevo en Explowords?',
        fr: 'Nouveau sur Explowords ?',
        pt: 'Novo no Explowords?',
        it: 'Nuovo su Explowords?',
    },
    'tutorial.offerMessage': {
        en: 'Would you like to play the interactive tutorial before your first match?',
        es: '¿Quieres jugar el tutorial interactivo antes de tu primera partida?',
        fr: 'Voulez-vous jouer au tutoriel interactif avant votre première partie ?',
        pt: 'Queres jogar o tutorial interativo antes da tua primeira partida?',
        it: 'Vuoi giocare il tutorial interattivo prima della tua prima partita?',
    },
    'tutorial.offerYes': {
        en: 'Play tutorial',
        es: 'Jugar tutorial',
        fr: 'Jouer au tutoriel',
        pt: 'Jogar tutorial',
        it: 'Gioca il tutorial',
    },
    'tutorial.offerNo': {
        en: 'Skip, start playing',
        es: 'Omitir, empezar a jugar',
        fr: 'Passer, commencer à jouer',
        pt: 'Saltar, começar a jogar',
        it: 'Salta, inizia a giocare',
    },
    'tutorial.completeTitle': {
        en: 'Tutorial complete',
        es: 'Tutorial completado',
        fr: 'Tutoriel terminé',
        pt: 'Tutorial concluído',
        it: 'Tutorial completato',
    },
    'tutorial.completeMessage': {
        en: 'You have finished the tutorial. You are ready to play Explowords — you can always replay it from the game mode menu.',
        es: 'Has terminado el tutorial. Estás listo para jugar a Explowords; siempre puedes jugarlo desde el menú de modo de juego.',
        fr: 'Vous avez terminé le tutoriel. Vous êtes prêt à jouer à Explowords — vous pouvez le rejouer depuis le menu des modes.',
        pt: 'Concluíste o tutorial. Estás pronto para jogar Explowords — podes repetí-lo a partir do menu de modos.',
        it: 'Hai finito il tutorial. Sei pronto per giocare a Explowords — puoi ripeterlo dal menu modalità.',
    },
    'tutorial.completeOk': {
        en: 'Got it',
        es: 'Entendido',
        fr: 'Compris',
        pt: 'Percebi',
        it: 'Capito',
    },
};

let currentLocale: Locale = 'en';

function getStoredLocale(): Locale | null {
    try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
        if (raw && (raw === 'en' || raw === 'es' || raw === 'fr' || raw === 'pt' || raw === 'it')) return raw as Locale;
    } catch {
        // ignore
    }
    return null;
}

/** Map navigator.language to a supported locale (defaults to English). */
export function detectBrowserLocale(): Locale {
    if (typeof navigator === 'undefined') return 'en';
    const lang = (navigator.language || (navigator.languages && navigator.languages[0]) || 'en')
        .toLowerCase()
        .split('-')[0];
    if (lang === 'es' || lang === 'fr' || lang === 'pt' || lang === 'it') return lang;
    return 'en';
}

export function getLocale(): Locale {
    return currentLocale;
}

export function setLocale(locale: Locale): void {
    currentLocale = locale;
    try {
        if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, locale);
    } catch {
        // ignore
    }
}

function readLocaleFromQuery(): Locale | null {
    try {
        if (typeof window === 'undefined') return null;
        const raw = new URLSearchParams(window.location.search).get('lang');
        if (raw === 'en' || raw === 'es' || raw === 'fr' || raw === 'pt' || raw === 'it') return raw;
    } catch {
        // ignore
    }
    return null;
}

export function initLocale(): void {
    const fromQuery = readLocaleFromQuery();
    if (fromQuery) {
        setLocale(fromQuery);
        return;
    }
    const stored = getStoredLocale();
    if (stored) {
        currentLocale = stored;
        return;
    }
    currentLocale = detectBrowserLocale();
    setLocale(currentLocale);
}

export function t(key: string): string {
    const row = translations[key];
    if (!row) return key;
    return row[currentLocale] ?? row.en ?? key;
}

export function fill(key: string, vars: Record<string, string>): string {
    let s = t(key);
    for (const [k, v] of Object.entries(vars)) {
        s = s.replaceAll(`{${k}}`, v);
    }
    return s;
}

export function getLanguageLabel(locale: Locale): string {
    return LANGUAGES.find((l) => l.code === locale)?.label ?? locale;
}

export function getLanguageFlag(locale: Locale): string {
    return LANGUAGES.find((l) => l.code === locale)?.flag ?? '🌐';
}

/** Cycle to next language in LANGUAGES order. */
export function cycleLocale(): Locale {
    const idx = LANGUAGES.findIndex((l) => l.code === currentLocale);
    const next = LANGUAGES[(idx + 1) % LANGUAGES.length];
    currentLocale = next.code;
    setLocale(currentLocale);
    return currentLocale;
}

/** Update all elements with data-i18n="key" to current locale. */
export function applyTranslations(): void {
    if (typeof document === 'undefined') return;
    document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
        const key = el.getAttribute('data-i18n');
        if (key) el.textContent = t(key);
    });
    // Elements that need title/placeholder
    document.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
        const key = el.getAttribute('data-i18n-title');
        if (key) el.setAttribute('title', t(key));
    });
    document.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder]').forEach((el) => {
        const key = el.getAttribute('data-i18n-placeholder');
        if (key) el.placeholder = t(key);
    });
}
